import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CORE_SERVICES } from "../../data/content";

export default function CoreServices() {
  const epoxy = CORE_SERVICES.find((s) => s.slug === "epoxy-flooring");
  const others = CORE_SERVICES.filter((s) => s.slug !== "epoxy-flooring");

  return (
    <section
      data-testid="core-services"
      id="services"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14 md:mb-20">
          <div className="md:col-span-7">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-ink-muted mb-6">
              Our Core Services
            </div>
            <h2 className="font-display text-4xl md:text-6xl text-ink leading-[1.02] tracking-[-0.03em]">
              Three disciplines.
              <br />
              <span className="font-serif-italic font-normal">
                One team on site.
              </span>
            </h2>
          </div>
          <p className="md:col-span-5 self-end text-ink-muted text-base md:text-lg leading-relaxed max-w-md md:ml-auto">
            Every project begins with a site inspection. We prepare the surface
            properly, select an appropriate system for the actual conditions,
            and finish with clean, quality-focused workmanship.
          </p>
        </div>

        {/* Flagship Epoxy card (wide) + 2 stacked smaller cards */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
          {/* Flagship Epoxy — spans 8/12 and tall */}
          <Link
            to={epoxy.href}
            data-testid={`service-card-${epoxy.slug}`}
            className="group relative md:col-span-8 md:row-span-2 overflow-hidden bg-ink border border-ink-line hover:border-orange transition-all"
          >
            {/* Orange accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-orange z-20" />

            <div className="relative aspect-[16/10] md:aspect-auto md:h-[440px] overflow-hidden">
              <img
                src={epoxy.image}
                alt={epoxy.title}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
              <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
                <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-white/85 bg-ink/50 backdrop-blur px-3 py-1.5">
                  Flagship Service
                </span>
                {epoxy.badge && (
                  <span
                    data-testid={`service-badge-${epoxy.slug}`}
                    className="font-mono text-[11px] tracking-[0.25em] uppercase text-white bg-orange px-3 py-1.5"
                  >
                    {epoxy.badge}
                  </span>
                )}
              </div>

              {/* On-image copy */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 text-white">
                <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-orange-light mb-3">
                  {epoxy.n} — Epoxy Flooring · Pan India
                </div>
                <h3 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-[-0.02em] max-w-xl">
                  <span className="text-orange">Epoxy Flooring.</span>
                  <br />
                  <span className="font-serif-italic font-normal">
                    Seamless. Durable.
                  </span>
                </h3>
                <p className="mt-4 text-white/75 text-sm md:text-base max-w-lg leading-relaxed">
                  Industrial, warehouse, factory, workshop, showroom and
                  commercial epoxy floors — delivered across Bangalore,
                  Hyderabad, Kolkata, Pune, Chennai, Baddi, Ahmedabad &amp;
                  Gurgaon.
                </p>
                <div className="mt-6 inline-flex items-center gap-3 border-b border-orange pb-1 font-mono text-[11px] tracking-[0.2em] uppercase text-orange-light group-hover:text-orange transition-colors">
                  {epoxy.cta}
                  <ArrowUpRight
                    size={16}
                    className="transition-transform group-hover:rotate-45"
                  />
                </div>
              </div>
            </div>
          </Link>

          {/* Two smaller cards — Waterproofing + Painting */}
          {others.map((s) => (
            <Link
              key={s.slug}
              to={s.href}
              data-testid={`service-card-${s.slug}`}
              className="group relative md:col-span-4 bg-white border border-ink-line overflow-hidden transition-all hover:border-ink"
            >
              <div className="relative aspect-[5/3] md:aspect-[16/9] overflow-hidden bg-ink">
                <img
                  src={s.image}
                  alt={s.title}
                  loading="lazy"
                decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/40 to-transparent" />
                <span className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.25em] uppercase text-white/90 bg-ink/40 backdrop-blur px-2.5 py-1">
                  {s.n} — Service
                </span>
                {s.badge && (
                  <span
                    data-testid={`service-badge-${s.slug}`}
                    className="absolute top-3 right-3 font-mono text-[10px] tracking-[0.2em] uppercase bg-white text-ink px-2.5 py-1"
                  >
                    {s.badge}
                  </span>
                )}
              </div>
              <div className="p-5 md:p-6">
                <h3 className="font-display text-xl md:text-2xl text-ink tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-2 text-ink-mid text-sm leading-relaxed">
                  {s.lead}
                </p>
                <div className="mt-4 pt-4 border-t border-ink-line flex items-center justify-between">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink group-hover:text-ink-soft">
                    {s.cta}
                  </span>
                  <ArrowUpRight
                    size={16}
                    className="text-ink transition-transform group-hover:rotate-45"
                  />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
